import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import useEmblaCarousel from 'embla-carousel-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store/use-store'
import useStableViewport from '@/hooks/use-stable-viewport'

import Section from '../base/section'
import { cn } from '@/lib/utils'

gsap.registerPlugin(ScrollTrigger)

type SustainabilityNumbersProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    items: {
      title?: string
      text?: string
    }[]
  }>

const SustainabilityNumber = ({
  index,
  item,
  className
}: {
  index: number
  item: { title?: string; text?: string }
  className?: string
}) => (
  <div
    className={cn(
      'item relative flex cursor-pointer flex-col gap-spacing-2xl bg-[#E5E5E5] !p-spacing-lg',
      className
    )}
  >
    <Typography variant="small" className="condensed">
      {(index + 1).toString().padStart(2, '0')}
    </Typography>
    <div className="flex flex-col gap-spacing-sm">
      <Typography variant="h3">{item.title}</Typography>
      <Typography variant="regular" className="flex text-text-primary-light">
        {item.text}
      </Typography>
    </div>
  </div>
)

const SustainabilityNumbers = ({
  title,
  items: collection,
  ...props
}: SustainabilityNumbersProps) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)
  const { isMobile } = useStableViewport()
  const [activeIndex, setActiveIndex] = useState(0)

  const { strings } = useStore()

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    active: (collection?.length ?? 0) > 1
  })

  useEffect(() => {
    if (!emblaApi || !isMobile) return

    const onSelect = () => {
      setActiveIndex(emblaApi.selectedScrollSnap())
    }

    emblaApi.on('select', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, isMobile])

  useGSAP(
    () => {
      if (isMobile) return

      const sticky = stickyRef.current
      const section = sectionRef.current

      if (!sticky || !section || !collection) return

      const selector = gsap.utils.selector(sticky)

      const items = selector('[data-items]')[0]
      const titleEl = selector('[data-title]')[0]

      const itemsWidth = items.scrollWidth

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          toggleActions: 'play reverse play reverse',
          scrub: true,
          invalidateOnRefresh: true
        }
      })

      titleEl &&
        timeline.to(titleEl, {
          x: '16vw',
          ease: 'none'
        })

      items &&
        timeline.to(
          items,
          {
            x: -itemsWidth - window.innerWidth * 0.5,
            ease: 'none'
          },
          0
        )

      return () => {
        timeline.kill()
      }
    },

    {
      scope: stickyRef,
      dependencies: [collection, title, isMobile],
      revertOnUpdate: true
    }
  )

  // Mobile: GSAP fadeIn
  const mobileMotionRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      if (!isMobile || !mobileMotionRef.current) return

      gsap.fromTo(
        mobileMotionRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: mobileMotionRef.current,
            once: true,
            start: 'top 82%',
            invalidateOnRefresh: true,
            toggleActions: 'play none none none'
          }
        }
      )
    },
    {
      scope: mobileMotionRef,
      dependencies: [collection, isMobile],
      revertOnUpdate: true
    }
  )

  if (!collection?.length) return null

  const renderDesktop = () => (
    <Section
      ref={sectionRef}
      {...props}
      className="relative max-w-full"
      style={{
        height: `calc((var(--lvh))*${collection.length * 80})`
      }}
    >
      <div
        ref={stickyRef}
        data-gallery
        className="sticky top-0 h-[calc(100*var(--lvh))] w-full max-w-full overflow-hidden"
      >
        <div
          data-gallery
          data-horizontal-wheel-lock
          className="overflow-x-clip"
        >
          {title ? (
            <div
              className={cn(
                'pointer-events-none absolute left-0 top-0 z-0 flex h-full w-full items-center',
                'translate-x-[50vw]'
              )}
              data-title
            >
              <div
                className={cn(
                  'whitespace-nowrap opacity-10',
                  'text-[139px] md:text-d-huge',
                  'text-text-primary-light dark:text-text-primary-dark'
                )}
              >
                {title || strings?.labels?.gallery_details}
              </div>
            </div>
          ) : null}

          <div
            className="flex h-[calc(100*var(--lvh))] items-center gap-spacing-2xs"
            data-items
          >
            {collection.map((item, index) => (
              <SustainabilityNumber
                key={`sustainability-number-${index}`}
                index={index}
                item={item}
                className={cn(
                  'item relative cursor-pointer bg-[#E5E5E5]',
                  'h-[224px] w-[335px] flex-[0_0_335px]',
                  'md:h-[299px] md:w-[533px] md:flex-[0_0_533px]',
                  'translate-x-[100vw]'
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </Section>
  )

  const renderMobile = () => (
    <Section {...props} className="px-padding-sm py-spacing-xl">
      <div ref={mobileMotionRef} data-motion-gallery className="opacity-0">
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {collection.map((item, index) => (
              <div
                key={`sustainability-number-mobile-${index}`}
                className="w-full shrink-0"
              >
                <SustainabilityNumber
                  index={index}
                  item={item}
                  className="h-full w-full"
                />
              </div>
            ))}
          </div>
        </div>
        {collection.length > 1 && (
          <div className="flex items-center justify-between pt-spacing-lg">
            <Typography variant="large" className="condensed">
              {(activeIndex + 1).toString().padStart(2, '0')}/
              {collection.length.toString().padStart(2, '0')}
            </Typography>
            <div className="flex items-end justify-end gap-spacing-2xs self-end">
              <button
                className="flex size-[32px] items-center justify-center rounded-radius-minimal border border-border-secondary-light dark:border-border-secondary-dark"
                onClick={() => emblaApi?.scrollPrev()}
                aria-label={
                  strings?.labels?.gallery_prev || 'Elemento precedente'
                }
              >
                <SVGIcon symbol="arrow-prev" className="size-[12px]" />
              </button>
              <button
                className="flex size-[32px] items-center justify-center rounded-radius-minimal border border-border-secondary-light dark:border-border-secondary-dark"
                onClick={() => emblaApi?.scrollNext()}
                aria-label={
                  strings?.labels?.gallery_next || 'Elemento successivo'
                }
              >
                <SVGIcon symbol="arrow-next" className="size-[12px]" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  )

  return <>{isMobile ? renderMobile() : renderDesktop()}</>
}

export default SustainabilityNumbers
